import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../components/ui/use-toast';
import { useT } from '../../context/LanguageContext';
import loyaltyService from '../../services/loyaltyService';
import { getAllDishes } from '../../services/menuService';
import { useRestaurant } from '../../context/RestaurantContext';
import { Gift, Plus, Pencil, Ticket, Search } from 'lucide-react';

/*
 * The rewards catalogue — what points BUY here. A hospitality reward names a
 * dish or an amount; the form asks only for what the chosen kind needs.
 * Retiring is a deactivate, never a delete: vouchers point at these rows.
 *
 * The voucher tools live on this page too (lookup / redeem / void): the
 * catalogue mints vouchers, so the screen that manages one manages the other.
 */

const EMPTY = {
  name: '', description: '', kind: 'FREE_DISH', points_cost: '',
  percent_value: '', amount_value: '', validity_days: 30,
  per_member_limit: 0, total_stock: 0, minimum_bill: '', dish: '',
};

const VOUCHER_CLS = {
  ACTIVE: 'bg-emerald-100 text-emerald-800',
  REDEEMED: 'bg-gray-100 text-gray-500',
  EXPIRED: 'bg-amber-100 text-amber-700',
  VOID: 'bg-red-50 text-red-600',
};

export default function RewardsCataloguePage() {
  const { t } = useT();
  const { toast } = useToast();
  const { restaurantId } = useRestaurant();
  const [items, setItems] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [editing, setEditing] = useState(null);   // null | {…form}
  const [busy, setBusy] = useState(false);

  // Voucher till tools
  const [vCode, setVCode] = useState('');
  const [voucher, setVoucher] = useState(null);

  const load = useCallback(() => {
    loyaltyService.listRewards()
      .then(setItems)
      .catch((err) => toast({ title: 'Error', description: err.message, variant: 'destructive' }));
  }, [toast]);

  useEffect(() => {
    load();
    // The dish picker for FREE_DISH rewards. Tolerant: the catalogue still
    // works if the dish list is slow or empty — getAllDishes returns [] on
    // any failure by its own contract.
    if (restaurantId) {
      getAllDishes(restaurantId).then((d) => setDishes(d || []));
    }
  }, [load, restaurantId]);

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const body = {
        name: editing.name,
        description: editing.description,
        kind: editing.kind,
        points_cost: editing.points_cost,
        percent_value: editing.percent_value || 0,
        amount_value: editing.amount_value || 0,
        validity_days: editing.validity_days || 0,
        per_member_limit: editing.per_member_limit || 0,
        total_stock: editing.total_stock || 0,
        minimum_bill: editing.minimum_bill || 0,
        dish: editing.kind === 'FREE_DISH' ? (editing.dish || null) : null,
      };
      if (editing.id) await loyaltyService.saveReward(editing.id, body);
      else await loyaltyService.createReward(body);
      toast({ title: editing.id ? t('loyalty.reward.edit') : t('loyalty.reward.add'), description: editing.name });
      setEditing(null);
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const retire = async (item) => {
    try {
      await loyaltyService.deleteReward(item.id);
      load();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const lookupVoucher = async (e) => {
    e?.preventDefault();
    setVoucher(null);
    try {
      setVoucher(await loyaltyService.voucherLookup(vCode));
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const redeemVoucher = async () => {
    try {
      const v = await loyaltyService.redeemVoucher({ code: voucher.code });
      setVoucher(v);
      toast({ title: t('loyalty.voucher.redeem'), description: `${v.title} · ${v.code}` });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const voidVoucher = async () => {
    try {
      const v = await loyaltyService.voidVoucher(voucher.id, {});
      setVoucher(v);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const kindFields = (form, set) => {
    switch (form.kind) {
      case 'FREE_DISH':
        return (
          <select className="w-full rounded-md border px-3 py-2 text-sm bg-background"
            value={form.dish || ''} required
            onChange={(e) => set({ ...form, dish: e.target.value })}>
            <option value="">Pick the dish…</option>
            {dishes.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        );
      case 'PERCENT_OFF':
        return <Input type="number" step="0.5" min="0.5" max="100" required
          placeholder="% off" value={form.percent_value}
          onChange={(e) => set({ ...form, percent_value: e.target.value })} />;
      case 'AMOUNT_OFF':
        return <Input type="number" step="0.01" min="1" required
          placeholder="Amount off (Rs)" value={form.amount_value}
          onChange={(e) => set({ ...form, amount_value: e.target.value })} />;
      default:
        return null;   // EXPERIENCE needs nothing but its name
    }
  };

  return (
    <div className="p-6 w-full">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none flex items-center gap-2">
          <Gift className="h-7 w-7 text-amber-600" /> {t('loyalty.rewards')}
        </h1>
        <Button onClick={() => setEditing({ ...EMPTY })}
          className="bg-amber-600 hover:bg-amber-700 text-white">
          <Plus size={15} className="mr-1" /> {t('loyalty.reward.add')}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* The catalogue */}
        <Card className="p-5 lg:col-span-2">
          {items.length === 0 ? (
            <p className="text-sm text-gray-500">{t('loyalty.common.none')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-gray-500">
                  <tr>
                    <th className="text-left font-medium py-2 pr-4">{t('loyalty.common.name')}</th>
                    <th className="text-left font-medium py-2 pr-4">Type</th>
                    <th className="text-right font-medium py-2 pr-4">{t('loyalty.reward.cost')}</th>
                    <th className="text-right font-medium py-2 pr-4">{t('loyalty.reward.stock')}</th>
                    <th className="text-right font-medium py-2">{t('loyalty.common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((r) => (
                    <tr key={r.id} className={r.is_active ? '' : 'opacity-50'}>
                      <td className="py-2 pr-4">
                        <div className="font-medium text-gray-900">
                          {r.name}
                          {!r.is_active && (
                            <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500">
                              {t('loyalty.reward.retired')}
                            </span>
                          )}
                        </div>
                        {r.dish_name && <div className="text-xs text-gray-400">{r.dish_name}</div>}
                      </td>
                      <td className="py-2 pr-4">{t(`loyalty.reward.kind.${r.kind}`)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums font-semibold">
                        {Number(r.points_cost).toLocaleString()}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums text-gray-500">
                        {r.total_stock
                          ? `${r.issued_count}/${r.total_stock}`
                          : `${r.issued_count} ${t('loyalty.reward.claimed')}`}
                      </td>
                      <td className="py-2 text-right whitespace-nowrap">
                        <button type="button" title={t('loyalty.reward.edit')}
                          onClick={() => setEditing({
                            ...EMPTY, ...r, dish: r.dish || '',
                          })}
                          className="p-1 rounded hover:bg-accent text-gray-600"><Pencil size={15} /></button>
                        {r.is_active && (
                          <button type="button" onClick={() => retire(r)}
                            className="ml-2 text-xs text-gray-500 hover:underline">
                            {t('loyalty.reward.retired')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Voucher till tools */}
        <Card className="p-5">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-1.5">
            <Ticket size={16} className="text-amber-600" /> {t('loyalty.voucher.code')}
          </h3>
          <form onSubmit={lookupVoucher} className="flex gap-2">
            <Input placeholder="RWD-XXXXXXXX" value={vCode}
              onChange={(e) => setVCode(e.target.value)} />
            <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white shrink-0">
              <Search size={15} />
            </Button>
          </form>
          {voucher && (
            <div className="mt-4 rounded-lg border p-3 text-sm space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-gray-900">{voucher.title}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${VOUCHER_CLS[voucher.status] || ''}`}>
                  {t(`loyalty.voucher.status.${voucher.status}`)}
                </span>
              </div>
              <div className="text-gray-500">{voucher.member_name} · <span className="font-mono text-xs">{voucher.member}</span></div>
              {voucher.expires_at && <div className="text-gray-500">Expires {new Date(voucher.expires_at).toLocaleDateString()}</div>}
              {voucher.value_applied && (
                <div className="text-emerald-700 font-semibold">− {voucher.value_applied}</div>
              )}
              {voucher.status === 'ACTIVE' && (
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={redeemVoucher}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    {t('loyalty.voucher.redeem')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={voidVoucher}>
                    {t('loyalty.voucher.void')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Reward form */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !busy && setEditing(null)}>
          <Card className="w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {editing.id ? t('loyalty.reward.edit') : t('loyalty.reward.add')}
            </h2>
            <form onSubmit={save} className="space-y-3">
              <Input placeholder={`${t('loyalty.common.name')} *`} required value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <Input placeholder="Description" value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <select className="rounded-md border px-3 py-2 text-sm bg-background"
                  value={editing.kind}
                  onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                  {['FREE_DISH', 'PERCENT_OFF', 'AMOUNT_OFF', 'EXPERIENCE'].map((k) => (
                    <option key={k} value={k}>{t(`loyalty.reward.kind.${k}`)}</option>
                  ))}
                </select>
                <Input type="number" min="1" required placeholder={`${t('loyalty.reward.cost')} *`}
                  value={editing.points_cost}
                  onChange={(e) => setEditing({ ...editing, points_cost: e.target.value })} />
              </div>
              {kindFields(editing, setEditing)}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[11px] text-gray-500">Voucher lives (days)</label>
                  <Input type="number" min="0" value={editing.validity_days}
                    onChange={(e) => setEditing({ ...editing, validity_days: e.target.value })} />
                </div>
                <div>
                  <label className="text-[11px] text-gray-500">Per member (0 = ∞)</label>
                  <Input type="number" min="0" value={editing.per_member_limit}
                    onChange={(e) => setEditing({ ...editing, per_member_limit: e.target.value })} />
                </div>
                <div>
                  <label className="text-[11px] text-gray-500">{t('loyalty.reward.stock')} (0 = ∞)</label>
                  <Input type="number" min="0" value={editing.total_stock}
                    onChange={(e) => setEditing({ ...editing, total_stock: e.target.value })} />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" disabled={busy}
                  onClick={() => setEditing(null)}>{t('loyalty.common.cancel')}</Button>
                <Button type="submit" disabled={busy}
                  className="bg-amber-600 hover:bg-amber-700 text-white">{t('loyalty.common.save')}</Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
